import { ChatRoomMessage } from "@/models/chat.model.ts";
import { uuid } from "@/interfaces";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface RoomState {
  list: ChatRoomMessage[];
  fullyLoaded: boolean;
  loading: boolean;
}

interface State {
  byRoom: Record<uuid, RoomState>;
}

const initialState: State = { byRoom: {} };

const slice = createSlice({
  name: "chatRooms",
  initialState,
  reducers: {
    appendOlder(state, action: PayloadAction<{ roomId: uuid; messages: ChatRoomMessage[]; fullyLoaded: boolean }>) {
      const { roomId, messages, fullyLoaded } = action.payload;

      if (!state.byRoom[roomId]) state.byRoom[roomId] = { list: [], loading: false, fullyLoaded };

      const existingIds = new Set(state.byRoom[roomId].list.map((m) => m.id));
      state.byRoom[roomId].list = [...messages.filter((m) => !existingIds.has(m.id)), ...state.byRoom[roomId].list];

      state.byRoom[roomId].fullyLoaded = fullyLoaded;
      state.byRoom[roomId].loading = false;
    },

    setLoading(state, action: PayloadAction<{ roomId: uuid; loading: boolean }>) {
      const rs = state.byRoom[action.payload.roomId] ?? {
        list: [],
        fullyLoaded: false,
        loading: false,
      };

      rs.loading = action.payload.loading;
      state.byRoom[action.payload.roomId] = rs;
    },

    addNew(state, action: PayloadAction<{ roomId: uuid; message: ChatRoomMessage }>) {
      const { roomId, message } = action.payload;

      if (!state.byRoom[roomId]) state.byRoom[roomId] = { list: [], loading: false, fullyLoaded: false };

      if (state.byRoom[roomId].list.some((m) => m.id === message.id)) return;
      state.byRoom[roomId].list.push(message);
    },

    resetRoom(state, action: PayloadAction<uuid>) {
      delete state.byRoom[action.payload];
    },

    replaceTemp(state, action: PayloadAction<{ roomId: uuid; tempId: string; real: ChatRoomMessage; temp?: any }>) {
      const rs = state.byRoom[action.payload.roomId];
      if (!rs) return;

      const idx = rs.list.findIndex((m) => m.id === action.payload.tempId);
      if (idx === -1) return;
      const alreadyListed = rs.list.some((m, at) => at !== idx && m.id === action.payload.real.id);
      if (alreadyListed) rs.list.splice(idx, 1);
      else rs.list[idx] = action.payload.real;
    },

    removeTemp(state, action: PayloadAction<{ roomId: uuid; tempId: string }>) {
      const rs = state.byRoom[action.payload.roomId];
      if (!rs) return;

      rs.list = rs.list.filter((m) => m.id !== action.payload.tempId);
    },
  },
});

export const { appendOlder, setLoading, addNew, resetRoom, replaceTemp, removeTemp } = slice.actions;
export default slice.reducer;
